import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { BUSINESS_TYPES } from '../lib/constants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { getBots, createBot, updateBot, deleteBot, toggleBotStatus } from '@/lib/api';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Bot,
  Play,
  Pause,
  MessageSquare,
  Ticket,
  Phone,
  Building2,
  Zap,
  Loader2,
  ShieldCheck,
  Smartphone,
  Database,
  Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fluxoco_token';



const BotsPage = () => {
  const location = useLocation();
  const [bots, setBots] = useState([]);
  const [isActivating, setIsActivating] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTenant, setFilterTenant] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  const [availableSilos, setAvailableSilos] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    business_type: 'otro',
    tenant_id: '',
    welcome_message: '¡Hola! Soy tu asistente virtual. ¿En qué puedo ayudarte?',
    language: 'es',
    tone: 'profesional',
    twilio_sid: '',
    twilio_auth_token: '',
    twilio_phone: '',
    flows_enabled: {
      customer_support: true,
      ticket_creation: true,
      technician_channel: false,
      auto_reports: false
    },
    allowed_silos: []
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchData();
    
    // Check for URL parameters (deep link from Tenants Page)
    const params = new URLSearchParams(location.search);
    const tenantId = params.get('tenant_id');
    const tenantName = params.get('tenant_name');
    const bizType = params.get('business_type');

    if (tenantId) {
      setFormData(prev => ({
        ...prev,
        tenant_id: tenantId,
        name: tenantName ? `Asistente ${decodeURIComponent(tenantName)}` : prev.name,
        business_type: bizType || prev.business_type
      }));
      setIsCreateOpen(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (formData.tenant_id) {
      fetchAvailableSilos(formData.tenant_id);
    }
  }, [formData.tenant_id]);

  const fetchAvailableSilos = async (tenantId) => {
    try {
      const response = await axios.get(`${API_URL}/api/tenants/${tenantId}/collections`, { headers: getAuthHeaders() });
      setAvailableSilos(response.data);
    } catch (error) {
      console.error("Error fetching available silos:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [botsRes, tenantsRes, verticalsRes] = await Promise.all([
        getBots(),
        axios.get(`${API_URL}/api/admin/tenants`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/verticals`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setBots(botsRes.data);
      setTenants(tenantsRes.data);
      setVerticals(verticalsRes.data);
    } catch (error) {
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.business_type || !formData.tenant_id) {
      toast.error('Por favor completa los campos requeridos (Nombre, Tipo, Tenant)');
      return;
    }

    try {
      await createBot(formData);
      toast.success('Bot creado exitosamente');
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al crear el bot');
    }
  };

  const handleUpdate = async () => {
    if (!selectedBot) return;

    try {
      await updateBot(selectedBot.id, formData);
      toast.success('Bot actualizado exitosamente');
      setIsEditOpen(false);
      setSelectedBot(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar el bot');
    }
  };

  const handleActivate = async (bot) => {
    setIsActivating(bot.id);
    try {
      const response = await axios.post(`${API_URL}/api/v1/bots/${bot.id}/activate`, {}, { headers: getAuthHeaders() });
      if (response.data.success) {
        toast.success(`¡Bot "${bot.name}" activado y vinculado correctamente!`);
        fetchData();
      }
    } catch (error) {
      console.error('Error activating bot:', error);
      toast.error(error.response?.data?.detail || 'Error al conectar con la fábrica de bots');
    } finally {
      setIsActivating(null);
    }
  };

  const handleDelete = async (bot) => {
    if (!confirm(`¿Estás seguro de eliminar el bot "${bot.name}"?`)) return;

    try {
      await deleteBot(bot.id);
      toast.success('Bot eliminado exitosamente');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el bot');
    }
  };

  const handleToggleStatus = async (bot) => {
    try {
      const response = await toggleBotStatus(bot.id);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Error al cambiar el estado del bot');
    }
  };

  const openEdit = (bot) => {
    setSelectedBot(bot);
    setFormData({
      name: bot.name,
      business_type: bot.business_type,
      tenant_id: bot.tenant_id || '',
      welcome_message: bot.welcome_message,
      language: bot.language || 'es',
      tone: bot.tone || 'profesional',
      twilio_sid: bot.twilio_sid || '',
      twilio_auth_token: bot.twilio_auth_token || '',
      twilio_phone: bot.twilio_phone || '',
      flows_enabled: bot.flows_enabled || {
        customer_support: true,
        ticket_creation: true,
        technician_channel: false,
        auto_reports: false
      },
      allowed_silos: bot.allowed_silos || []
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      business_type: '',
      tenant_id: '',
      twilio_sid: '',
      twilio_auth_token: '',
      twilio_phone: '',
      welcome_message: '¡Hola! Soy tu asistente virtual. ¿En qué puedo ayudarte?',
      language: 'es',
      tone: 'profesional',
      flows_enabled: {
        customer_support: true,
        ticket_creation: true,
        technician_channel: false,
        auto_reports: false
      },
      allowed_silos: []
    });
  };

  const filteredBots = bots.filter(bot => {
    const matchesSearch = (bot.name || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTenant = filterTenant === 'all' || bot.tenant_id === filterTenant;
    return matchesSearch && matchesTenant;
  });

  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant?.name || 'Sin tenant';
  };

  const renderBotForm = (isEdit = false) => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="space-y-2">
        <Label htmlFor="name">Nombre del Bot *</Label>
        <Input
          id="name"
          data-testid="bot-name-input"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ej: Asistente Taller García"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="tenant_id">Tenant (Cliente) *</Label>
        <Select
          value={formData.tenant_id}
          onValueChange={(value) => {
            const selectedTenant = tenants.find(t => t.id === value);
            const biz = selectedTenant ? (selectedTenant.vertical_id || selectedTenant.business_type || 'otro') : formData.business_type;
            setFormData({ ...formData, tenant_id: value, business_type: biz });
          }}
        >
          <SelectTrigger data-testid="bot-tenant-select">
            <SelectValue placeholder="Selecciona un tenant" />
          </SelectTrigger>
          <SelectContent>
            {tenants.map((tenant) => (
              <SelectItem key={tenant.id} value={tenant.id}>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {tenant.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!formData.tenant_id ? (
        <div className="space-y-2">
          <Label htmlFor="business_type">Tipo de Negocio *</Label>
          <Select
            value={formData.business_type}
            onValueChange={(value) => setFormData({ ...formData, business_type: value })}
          >
            <SelectTrigger data-testid="bot-business-type-select">
              <SelectValue placeholder="Selecciona un tipo" />
            </SelectTrigger>
            <SelectContent>
              {(verticals.length > 0 ? verticals : BUSINESS_TYPES).map((type) => (
                <SelectItem key={type.id || type.value} value={type.id || type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="space-y-2 opacity-70">
          <Label>Tipo de Negocio (Heredado del Tenant)</Label>
          <div className="h-10 px-3 py-2 bg-slate-900 border border-white/10 rounded-md text-sm text-slate-400 capitalize">
            {(() => {
              const v = verticals.find(v => v.id === formData.business_type);
              if (v) return v.label;
              const bt = BUSINESS_TYPES.find(b => b.value === formData.business_type);
              if (bt) return bt.label;
              return formData.business_type;
            })()}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="welcome_message">Mensaje de Bienvenida</Label>
        <Textarea
          id="welcome_message"
          data-testid="bot-welcome-message-input"
          value={formData.welcome_message}
          onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
          placeholder="Mensaje inicial del bot..."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="language">Idioma</Label>
          <Select
            value={formData.language}
            onValueChange={(value) => setFormData({ ...formData, language: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tone">Tono</Label>
          <Select
            value={formData.tone}
            onValueChange={(value) => setFormData({ ...formData, tone: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="profesional">Profesional</SelectItem>
              <SelectItem value="amigable">Amigable</SelectItem>
              <SelectItem value="formal">Formal</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* --- SECCIÓN DATA HUB ACCESS --- */}
      <div className="space-y-3 pt-4 border-t">
        <div className="flex items-center gap-2 text-cyan-400 mb-2">
            <Database className="w-4 h-4" />
            <span className="text-[10px] font-black uppercase tracking-widest leading-none">Data Hub Access (Silos)</span>
        </div>
        
        {Object.keys(availableSilos).length > 0 ? (
            <div className="grid grid-cols-1 gap-2 p-3 rounded-2xl bg-cyan-500/5 border border-cyan-500/10">
                {Object.entries(availableSilos).map(([key, config]) => (
                    <div key={key} className="flex items-center justify-between p-2 rounded-xl hover:bg-white/5 transition-colors">
                        <div className="flex flex-col">
                            <span className="text-xs font-bold text-slate-200">{config.display_name}</span>
                            <span className="text-[9px] text-slate-500 font-mono uppercase">{key}</span>
                        </div>
                        <Switch
                            checked={formData.allowed_silos.includes(key)}
                            onCheckedChange={(checked) => {
                                const newSilos = checked 
                                    ? [...formData.allowed_silos, key]
                                    : formData.allowed_silos.filter(s => s !== key);
                                setFormData({ ...formData, allowed_silos: newSilos });
                            }}
                        />
                    </div>
                ))}
            </div>
        ) : (
            <p className="text-[10px] text-slate-500 italic ml-1">
                {formData.tenant_id ? "Este tenant no tiene silos activos en el Data Hub." : "Selecciona un tenant para ver sus silos disponibles."}
            </p>
        )}
        <p className="text-[9px] text-slate-500 leading-relaxed italic ml-1">
          * El bot solo podrá consultar y registrar datos en los silos que selecciones arriba.
        </p>
      </div>

      <div className="space-y-3 pt-4 border-t">

        <Label>Flujos Habilitados</Label>
        <div className="space-y-3">
          {[
            { key: 'customer_support', label: 'Atención al Cliente 24/7' },
            { key: 'ticket_creation', label: 'Creación de Tickets' },
            { key: 'technician_channel', label: 'Canal de Técnicos' },
            { key: 'auto_reports', label: 'Reportes Automáticos' }
          ].map((flow) => (
            <div key={flow.key} className="flex items-center justify-between">
              <span className="text-sm">{flow.label}</span>
              <Switch
                checked={formData.flows_enabled[flow.key]}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    flows_enabled: { ...formData.flows_enabled, [flow.key]: checked }
                  })
                }
              />
            </div>
          ))}
        </div>
      </div>

      {/* --- SECCIÓN DE CONECTIVIDAD PRO (BOT LEVEL) --- */}
      <div className="col-span-2 mt-4 p-5 rounded-[2rem] bg-blue-500/5 border border-blue-500/10 space-y-4">
        <div className="flex items-center gap-2 text-blue-400">
          <ShieldCheck className="w-4 h-4" />
          <span className="text-[10px] font-black uppercase tracking-widest leading-none">Conectividad WhatsApp (Per Bot)</span>
        </div>
        
        <div className="grid grid-cols-2 gap-5">
          <div className="space-y-2 col-span-2">
            <Label className="text-[10px] text-slate-400 uppercase tracking-widest font-bold ml-1">Twilio Operating Phone (E.164)</Label>
            <div className="relative group">
              <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 transition-colors group-focus-within:text-blue-500" />
              <Input
                value={formData.twilio_phone}
                onChange={(e) => setFormData({ ...formData, twilio_phone: e.target.value })}
                placeholder="+5219381234567"
                className="bg-slate-950/50 border-white/10 rounded-2xl h-12 pl-12 text-white placeholder:text-slate-700 focus:border-blue-500/30 transition-all"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] text-slate-400 uppercase tracking-widest font-bold ml-1">Account SID</Label>
            <Input
              value={formData.twilio_sid}
              onChange={(e) => setFormData({ ...formData, twilio_sid: e.target.value })}
              placeholder="AC..."
              className="bg-slate-950/50 border-white/10 rounded-2xl h-12 text-white font-mono text-xs focus:border-blue-500/30 transition-all"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] text-slate-400 uppercase tracking-widest font-bold ml-1">Auth Token</Label>
            <Input
              type="password"
              value={formData.twilio_auth_token}
              onChange={(e) => setFormData({ ...formData, twilio_auth_token: e.target.value })}
              placeholder="••••••••"
              className="bg-slate-950/50 border-white/10 rounded-2xl h-12 text-white focus:border-blue-500/30 transition-all"
            />
          </div>
        </div>
        <p className="text-[9px] text-slate-500 leading-relaxed italic ml-1">
          * Cada bot puede tener su propio número independiente. Si este bot está vinculado a un Tenant, sus respuestas usarán estas llaves.
        </p>
      </div>

      <div className="pt-10">
        <button 
          onClick={isEdit ? handleUpdate : handleCreate}
          className="group relative overflow-hidden w-full h-16 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs uppercase tracking-[0.3em] rounded-2xl transition-all flex items-center justify-center gap-3 shadow-xl shadow-emerald-500/20 active:scale-[0.98]"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/0 via-white/10 to-emerald-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
          <Save className="w-5 h-5 relative z-10 group-hover:scale-110 transition-transform" /> 
          <span className="relative z-10">{isEdit ? 'Actualizar Bot AI' : 'Desplegar Nuevo Bot'}</span>
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-48 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bots-page">
      {/* Header V4.3 - ADN Fluxo Co */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 pt-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="flex items-center gap-2 mb-3">
            <Bot className="w-5 h-5 text-blue-500" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-blue-500/80">Fábrica de Asistentes</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit">
            Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Bots</span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium italic text-sm">Orquestación de inteligencia artificial para mensajería operativa</p>
        </motion.div>

        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-bot-btn" 
              className="group relative overflow-hidden rounded-2xl bg-blue-600 hover:bg-blue-500 text-white px-6 h-12 shadow-xl shadow-blue-500/20 transition-all duration-300 hover:shadow-blue-500/30 hover:scale-[1.02]"
              disabled={tenants.length === 0}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-blue-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              <Plus className="w-5 h-5 mr-2" />
              <span className="font-bold">Nuevo Bot</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl bg-[#0B1120] border-emerald-500/20 rounded-[3rem] p-0 overflow-hidden outline-none shadow-2xl">
            <div className="bg-gradient-to-br from-emerald-500/10 to-transparent p-10 max-h-[90vh] overflow-y-auto custom-scrollbar">
              <DialogHeader>
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 shadow-inner">
                    <Bot className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <DialogTitle className="font-outfit text-3xl font-black text-white italic tracking-tighter uppercase">Crear Nuevo Bot</DialogTitle>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Ingeniería de Inteligencia Artificial</p>
                  </div>
                </div>
              </DialogHeader>
              {renderBotForm()}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
          <Input
            data-testid="bot-search-input"
            placeholder="Buscar bots..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-950/50 border-white/5 rounded-xl h-11 text-white placeholder:text-slate-600 focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
          />
        </div>

        <Select value={filterTenant} onValueChange={setFilterTenant}>
          <SelectTrigger className="w-48" data-testid="filter-tenant-select">
            <SelectValue placeholder="Filtrar por tenant" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tenants</SelectItem>
            {tenants.map((tenant) => (
              <SelectItem key={tenant.id} value={tenant.id}>
                {tenant.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bots grid */}
      {tenants.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Bot className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="empty-state-title">Crea un tenant primero</h3>
          <p className="empty-state-description">
            Necesitas crear al menos un tenant antes de poder crear bots
          </p>
        </div>
      ) : filteredBots.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Bot className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="empty-state-title">No hay bots</h3>
          <p className="empty-state-description">
            {searchQuery 
              ? 'No se encontraron bots que coincidan con tu búsqueda'
              : 'Comienza creando tu primer bot para automatizar la atención'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBots.map((bot, index) => (
            <Card 
              key={bot.id} 
              className={`card-hover animate-fade-in stagger-${(index % 5) + 1}`}
              data-testid={`bot-card-${bot.id}`}
            >
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    bot.status === 'active' 
                      ? 'bg-gradient-to-br from-emerald-500 to-teal-500' 
                      : 'bg-gradient-to-br from-gray-500 to-gray-600'
                  }`}>
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-outfit">{bot.name}</CardTitle>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {getTenantName(bot.tenant_id)}
                    </p>
                  </div>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid={`bot-menu-${bot.id}`}>
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleToggleStatus(bot)}>
                      {bot.status === 'active' ? (
                        <>
                          <Pause className="w-4 h-4 mr-2" />
                          Pausar
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Activar
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openEdit(bot)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(bot)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </DropdownMenuItem>
                    {!bot.n8n_workflow_id && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleActivate(bot)}
                          className="bg-emerald-500/10 text-emerald-400 focus:bg-emerald-500/20 focus:text-emerald-400 font-bold"
                          disabled={isActivating === bot.id}
                        >
                          <Zap className="w-4 h-4 mr-2" />
                          {isActivating === bot.id ? 'Activando...' : 'Activar en Producción'}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>

              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {bot.welcome_message}
                  </p>

                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <MessageSquare className="w-4 h-4" />
                      <span>{bot.total_conversations || 0}</span>
                    </div>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Ticket className="w-4 h-4" />
                      <span>{bot.total_tickets || 0}</span>
                    </div>
                  </div>

                  {bot.whatsapp_number && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-4 h-4" />
                      <span className="text-xs">{bot.whatsapp_number}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      bot.status === 'active' ? 'status-active' : 'status-paused'
                    }`}>
                      {bot.status === 'active' ? 'Activo' : 'Pausado'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {verticals.find(v => v.id === bot.business_type)?.label || BUSINESS_TYPES.find(t => t.value === bot.business_type)?.label || bot.business_type}
                    </span>
                  </div>

                  {!bot.n8n_workflow_id && (
                    <Button 
                      onClick={() => handleActivate(bot)}
                      disabled={isActivating === bot.id}
                      className="w-full mt-4 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500/40 font-bold gap-2"
                      variant="outline"
                    >
                      {isActivating === bot.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Vinculando Cerebro...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 fill-emerald-400" />
                          Activar Producción
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-xl bg-[#0B1120] border-emerald-500/20 rounded-[3rem] p-0 overflow-hidden outline-none shadow-2xl">
          <div className="bg-gradient-to-br from-emerald-500/10 to-transparent p-10 max-h-[90vh] overflow-y-auto custom-scrollbar">
            <DialogHeader>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 shadow-inner">
                  <Edit className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <DialogTitle className="font-outfit text-3xl font-black text-white italic tracking-tighter uppercase">Editar Bot</DialogTitle>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Configuración Operativa del Asistente</p>
                </div>
              </div>
            </DialogHeader>
            {renderBotForm(true)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BotsPage;
